import React, { useRef, useEffect, useCallback, useState } from 'react';

export interface CollapseProps {
    isOpened: boolean;
    children: React.ReactNode;
    theme?: {
        collapse?: string;
        content?: string;
    };
    initialStyle?: {
        height?: string | number;
        overflow?: string;
    };
    onRest?: (state: CollapseState) => void;
    onWork?: (state: CollapseState) => void;
    checkTimeout?: number;
    id?: string;
    'aria-labelledby'?: string;
    'data-parent'?: string;
}

interface CollapseState {
    isFullyOpened: boolean;
    isFullyClosed: boolean;
    isOpened: boolean;
    containerHeight: number;
    contentHeight: number;
}

/**
 * Collapse component - optimized version using transitionend instead of polling.
 * Maintains the same API as react-collapse but with minimal reflows.
 */
const Collapse: React.FC<CollapseProps> = ({
    isOpened,
    children,
    theme = {
        collapse: 'ReactCollapse--collapse',
        content: 'ReactCollapse--content',
    },
    initialStyle: initialStyleProp,
    onRest,
    onWork,
    id,
    'aria-labelledby': ariaLabelledby,
    'data-parent': dataParent,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isAnimatingRef = useRef(false);
    const prevIsOpenedRef = useRef(isOpened);

    // Calculate initial style
    const [initialStyle] = useState<React.CSSProperties>(() => {
        if (initialStyleProp) {
            return initialStyleProp as React.CSSProperties;
        }
        return isOpened
            ? { height: 'auto', overflow: 'initial' }
            : { height: '0px', overflow: 'hidden' };
    });

    // Handle transition end
    const handleTransitionEnd = useCallback((e: TransitionEvent) => {
        // Only handle height transitions on the container itself
        if (e.propertyName !== 'height' || e.target !== containerRef.current) {
            return;
        }

        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) return;

        isAnimatingRef.current = false;

        const contentHeight = content.clientHeight;
        const containerHeight = container.clientHeight;

        // Set final state
        if (isOpened) {
            container.style.height = 'auto';
            container.style.overflow = 'initial';
        }

        onRest?.({
            isFullyOpened: isOpened,
            isFullyClosed: !isOpened,
            isOpened,
            containerHeight,
            contentHeight,
        });
    }, [isOpened, onRest]);

    // Handle isOpened changes
    useEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content) return;

        // Skip if isOpened hasn't changed
        if (prevIsOpenedRef.current === isOpened) return;
        prevIsOpenedRef.current = isOpened;

        const contentHeight = content.clientHeight; // Single reflow

        // If currently at 'auto', set explicit height first for transition
        if (container.style.height === 'auto') {
            container.style.height = `${contentHeight}px`;
        }

        // Force reflow to ensure the browser registers the starting height
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        container.offsetHeight;

        isAnimatingRef.current = true;
        container.style.overflow = 'hidden';

        // Set target height
        if (isOpened) {
            container.style.height = `${contentHeight}px`;
        } else {
            container.style.height = '0px';
        }

        onWork?.({
            isFullyOpened: false,
            isFullyClosed: false,
            isOpened,
            containerHeight: container.clientHeight,
            contentHeight,
        });
    }, [isOpened, onWork]);

    // Attach transitionend listener
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.addEventListener('transitionend', handleTransitionEnd);
        return () => {
            container.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, [handleTransitionEnd]);

    return (
        <div
            ref={containerRef}
            id={id}
            aria-labelledby={ariaLabelledby}
            data-parent={dataParent}
            className={theme?.collapse}
            style={initialStyle}
            aria-hidden={!isOpened}
        >
            <div ref={contentRef} className={theme?.content}>
                {children}
            </div>
        </div>
    );
};

export default Collapse;
